import PropTypes from 'prop-types';
import React from 'react';
import ActionButton from './ActionButton';
import '../../pages/TenderOpening/components/TenderOpening.css';

// Loading and empty states for list pages. Styled by the shared list-page
// classes (to-skeleton, to-empty*); see ListPager for the stylesheet note.
export const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

SkeletonRows.propTypes = { count: PropTypes.number };

// Empty state with a reason and a next step. Pick the copy by situation:
//   hasFilters     -> `filteredHint` + "Clear filters" (onClear)
//   no data at all -> `emptyHint` (why it is empty) + `action` (e.g. Create New)
// `action` and `secondaryAction` are { label, onClick, icon?, href? }; leave
// them out (or pass null) when the viewer cannot do anything about it.
// `compact` is for small inline tables, modals and dashboard cards.
export const EmptyState = ({
  title,
  emptyHint,
  filteredHint = 'Nothing matches the current search.',
  hasFilters = false,
  onClear,
  action,
  secondaryAction,
  icon = 'ri-file-search-line',
  compact = false,
}) => {
  const primary = !hasFilters && action;
  const secondary = !hasFilters && secondaryAction;
  return (
    <div
      className={compact ? 'to-empty to-empty-compact' : 'to-empty'}
      role='status'
    >
      <span className='to-empty-icon'>
        <i
          className={hasFilters ? 'ri-filter-off-line' : icon}
          aria-hidden='true'
        />
      </span>
      <h6 className='mb-1'>{title}</h6>
      <p className='text-muted fs-13 mb-3 to-empty-reason'>
        {hasFilters ? filteredHint : emptyHint}
      </p>
      <div className='to-empty-actions'>
        {hasFilters && onClear && (
          <ActionButton
            variant='secondary'
            icon='ri-filter-off-line'
            onClick={onClear}
          >
            Clear filters
          </ActionButton>
        )}
        {primary && (
          <ActionButton
            variant='primary'
            icon={primary.icon || 'ri-add-line'}
            onClick={primary.onClick}
          >
            {primary.label}
          </ActionButton>
        )}
        {secondary && (
          <ActionButton
            variant='tertiary'
            icon={secondary.icon}
            onClick={secondary.onClick}
          >
            {secondary.label}
          </ActionButton>
        )}
      </div>
    </div>
  );
};

const actionShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string,
});

EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  emptyHint: PropTypes.string,
  filteredHint: PropTypes.string,
  hasFilters: PropTypes.bool,
  onClear: PropTypes.func,
  action: actionShape,
  secondaryAction: actionShape,
  icon: PropTypes.string,
  compact: PropTypes.bool,
};
